/*
  <sticky-add-to-cart>

  Sticky bar size <select> that drives the main <product-variant-selects> picker.

  IMPORTANT: on every variant change the theme runs
      updateSourceFromDestination("ProductStickyCart")
  which replaces THIS element's innerHTML (destination.innerHTML = source.innerHTML)
  with a freshly server-rendered version (correct price, button state, and selected
  size). Because only the innerHTML is swapped, connectedCallback does NOT re-run, so
  any listener bound directly to the inner <select> is lost after the first change.

  Therefore:
    * We DELEGATE the change handler to the element itself (which persists), so it
      survives those innerHTML swaps.
    * We do NOT mirror price/button/selection in JS - the theme's innerHTML swap
      already does that from the server (single source of truth).
    * sticky -> main: on select change we drive the matching control in the LIVE
      picker (re-queried each time, since the picker is also replaced on change).
    * Visibility: shown whenever the main .product-form__buttons is out of the
      viewport; re-bound on each variant change in case that node is re-rendered.
*/
(function () {
  if (customElements.get('sticky-add-to-cart')) return;

  // Debug: run  window.__STICKY_DEBUG = true  in the console, then change the size.
  function debugOn() {
    try {
      return window.__STICKY_DEBUG === true || /[?&]stickydebug=1/.test(location.search);
    } catch (e) {
      return false;
    }
  }
  function log() {
    if (!debugOn()) return;
    try { console.log.apply(console, ['[sticky-atc]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function cssEscape(v) {
    if (window.CSS && CSS.escape) return CSS.escape(v);
    return String(v).replace(/["\\\]]/g, '\\$&');
  }

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;

      // Delegated change handler - survives the theme's innerHTML swaps of this element.
      if (!this._delegated) {
        this._delegated = true;
        this.addEventListener('change', (e) => {
          const sel = e.target && e.target.closest && e.target.closest('[data-sticky-size-select]');
          if (sel) this.onSelectChange(sel);
        });
      }

      // Re-bind the visibility observer whenever the variant (and thus the form) re-renders.
      var variantEvent =
        typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange
          ? PUB_SUB_EVENTS.variantChange
          : 'variant-change';
      if (typeof subscribe === 'function' && !this.variantUnsub) {
        this.variantUnsub = subscribe(variantEvent, () => {
          this.initVisibility();
          // Undo the theme's post-change focus()/scroll side effects when the change
          // came from the sticky. variantChange fires just BEFORE the theme focuses
          // the off-screen radio; rAF runs after that focus but before paint, so the
          // page never visibly jumps and focus returns to the (re-rendered) select.
          if (this._restore) {
            const r = this._restore;
            this._restore = null;
            const raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
            raf(() => {
              window.scrollTo(r.x, r.y);
              const sel = this.querySelector('[data-sticky-size-select]');
              if (sel) {
                try { sel.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
                window.scrollTo(r.x, r.y);
              }
            });
          }
        });
      }

      this.initVisibility();
    }

    disconnectedCallback() {
      if (this.variantUnsub) { this.variantUnsub(); this.variantUnsub = null; }
      if (this.io) { this.io.disconnect(); this.io = null; }
    }

    /* Always resolve the CURRENTLY connected picker (it is replaced on each change). */
    get picker() {
      return (
        document.querySelector('product-variant-selects[data-section="' + this.sectionId + '"]') ||
        document.querySelector('product-variant-selects')
      );
    }

    /* sticky select -> main picker */
    onSelectChange(select) {
      const picker = this.picker;
      if (!picker) { log('no live picker'); return; }
      const opt = select.options[select.selectedIndex];
      if (!opt) return;

      // The theme focuses the changed option's (off-screen) radio after re-render,
      // which on desktop scrolls to the top AND steals focus from this select
      // (causing the "needs two clicks" feel). Remember where we were so we can
      // undo both once the variant change completes.
      this._restore = { x: window.scrollX, y: window.scrollY };

      const valueStr = (opt.getAttribute('data-value') || '').trim();
      const valueId = (opt.value || '').trim();
      log('chose', valueStr, '(id ' + valueId + ')',
          '| checked BEFORE:', Array.from(picker.querySelectorAll('input:checked, option[selected]')).map((n) => (n.value || '').trim()));

      // Match the control in the LIVE picker - prefer the visible value string.
      const candidates = Array.from(picker.querySelectorAll('input.product-option__input, select option'));
      const node =
        candidates.find((n) => (n.value || '').trim() === valueStr) ||
        (valueId && picker.querySelector('[data-option-value-id="' + cssEscape(valueId) + '"]'));
      if (!node) { log('no matching node for', valueStr); return; }

      if (node.tagName === 'OPTION') {
        const sel = node.closest('select');
        if (!sel) return;
        Array.from(sel.options).forEach((o) => o.removeAttribute('selected'));
        node.setAttribute('selected', 'selected');
        sel.value = node.value;
        sel.dispatchEvent(new Event('input', { bubbles: true }));
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // radio: force an unambiguous group state (only touch this option's group)
        const groupName = node.getAttribute('name');
        if (groupName) {
          picker.querySelectorAll('input[name="' + cssEscape(groupName) + '"]').forEach((r) => {
            r.checked = r === node;
          });
        } else {
          node.checked = true;
        }
        node.dispatchEvent(new Event('input', { bubbles: true }));
        node.dispatchEvent(new Event('change', { bubbles: true }));
      }

      log('checked AFTER:', Array.from(picker.querySelectorAll('input:checked, option[selected]')).map((n) => (n.value || '').trim()));

      // Close the add-to-cart race: the form's variant id is only updated AFTER the
      // theme's async variant-change fetch resolves, so a fast click on the sticky
      // button during that window would submit the PREVIOUS size. Disable it now
      // (mirrors how the theme disables the main button via toggleSubmitButton).
      // It's re-enabled automatically when the theme re-renders this element's
      // innerHTML with the correct server button state once the new variant lands.
      const stickyBtn = this.querySelector('[data-sticky-atc]');
      if (stickyBtn) {
        stickyBtn.disabled = true;
        stickyBtn.setAttribute('aria-busy', 'true');
      }

      // Safety net: the server re-render normally replaces this button (clearing
      // aria-busy). If that never happens (e.g. a failed/aborted fetch), don't leave
      // it stuck disabled forever.
      clearTimeout(this._btnTimer);
      this._btnTimer = setTimeout(() => {
        const stuck = this.querySelector('[data-sticky-atc][aria-busy="true"]');
        if (stuck) {
          stuck.disabled = false;
          stuck.removeAttribute('aria-busy');
        }
      }, 6000);
    }

    /* show when the real Add-to-Cart is out of view */
    initVisibility() {
      if (this.io) { this.io.disconnect(); this.io = null; }
      const target =
        document.querySelector('#product-form-' + this.sectionId + ' .product-form__buttons') ||
        document.querySelector('.product-form__buttons');
      if (!target) return;
      this.io = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (window.Shopify && Shopify.designMode) return;
            this.classList.toggle('is-hidden', e.isIntersecting);
          }),
        { threshold: 0 }
      );
      this.io.observe(target);
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
})();
